import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';

import { DetailsTab, InventoryMappingTab, PackageInclusionsTab, PackageRegionsTab } from './PackageTabs';
import { PACKAGE_TABS } from './types';
import type { OfferBuilderPackage, PackageTab } from './types';
import { getMissingRequiredPackageFields, getPackageInclusionCount } from './utils';

const EMPTY_PACKAGES: Array<App.Package> = [];

interface Props {
  activePackageTab: PackageTab;
  availabilityRatesUrl?: string;
  expanded: boolean;
  isCleanDisplayNameDisabled?: boolean;
  isSaveDisabled?: boolean;
  isSaving?: boolean;
  isCleaningDisplayName?: boolean;
  offerId?: string;
  offerPackage: OfferBuilderPackage;
  onAutofill: () => void;
  onCleanDisplayName: () => void;
  onOpenInclusions: () => void;
  packages?: Array<App.Package>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  vendorId?: string;
  onChange: (nextPackageValues: Partial<App.Package> & Record<string, unknown>) => void;
  onExpandedChange: (isExpanded: boolean) => void;
  onSave: () => void;
  onTabChange: (tab: PackageTab) => void;
}

export default function SortableOfferPackageRow({
  activePackageTab,
  availabilityRatesUrl,
  expanded,
  isCleanDisplayNameDisabled,
  isCleaningDisplayName,
  isSaveDisabled,
  isSaving,
  offerId,
  offerPackage,
  onAutofill,
  onCleanDisplayName,
  onOpenInclusions,
  packages,
  propertyRoomTypes,
  vendorId,
  onChange,
  onExpandedChange,
  onSave,
  onTabChange,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: offerPackage.sortableId,
  });
  const inclusionCount = getPackageInclusionCount(offerPackage.package);
  const missingRequiredFields = getMissingRequiredPackageFields(offerPackage.package);

  return (
    <Accordion
      ref={setNodeRef}
      expanded={expanded}
      onChange={(_event, isExpanded) => onExpandedChange(isExpanded)}
      sx={{
        bgcolor: 'background.paper',
        opacity: isDragging ? 0.72 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <AccordionSummary component="div" expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%', pr: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <IconButton
              size="small"
              aria-label={`Reorder ${offerPackage.name}`}
              sx={{ cursor: isDragging ? 'grabbing' : 'grab', color: 'text.disabled' }}
              onClick={(event) => event.stopPropagation()}
              {...attributes}
              {...listeners}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6">{offerPackage.name}</Typography>
                <Chip
                  size="small"
                  color={offerPackage.statusColor}
                  variant="outlined"
                  label={offerPackage.statusLabel}
                />
                <Chip size="small" variant="outlined" label={offerPackage.packageSummary} />
                {missingRequiredFields.map((field) => (
                  <Chip
                    key={field.key}
                    size="small"
                    color="warning"
                    icon={<ErrorOutlineIcon />}
                    label={`${field.label} missing`}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
            {inclusionCount} {inclusionCount === 1 ? 'inclusion' : 'inclusions'}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Tabs
          value={activePackageTab}
          onChange={(_event, value) => onTabChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          {PACKAGE_TABS.map((tab) => (
            <Tab key={tab} value={tab} label={tab} icon={getPackageTabIcon(tab)} iconPosition="start" />
          ))}
        </Tabs>

        {activePackageTab === 'Details' && (
          <DetailsTab
            availabilityRatesUrl={availabilityRatesUrl}
            isCleanDisplayNameDisabled={isCleanDisplayNameDisabled}
            isCleaningDisplayName={isCleaningDisplayName}
            offerPackage={offerPackage.package}
            originalPackageName={offerPackage.originalPackageName}
            onAutofill={onAutofill}
            onCleanDisplayName={onCleanDisplayName}
            onChange={onChange}
          />
        )}
        {activePackageTab === 'Package inclusions' && (
          <PackageInclusionsTab
            offerId={offerId}
            offerPackage={offerPackage.package}
            onOpenInclusions={onOpenInclusions}
            packages={packages ?? EMPTY_PACKAGES}
            vendorId={vendorId}
            onChange={onChange}
          />
        )}
        {activePackageTab === 'Inventory mapping' && (
          <InventoryMappingTab
            offerPackage={offerPackage.package}
            propertyRoomTypes={propertyRoomTypes}
            onChange={onChange}
          />
        )}
        {activePackageTab === 'Package regions' && (
          <PackageRegionsTab offerPackage={offerPackage.package} onChange={onChange} />
        )}
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" disabled={isSaveDisabled || missingRequiredFields.length > 0} onClick={onSave}>
            {isSaving ? 'Saving...' : 'Save package'}
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function getPackageTabIcon(tab: PackageTab) {
  switch (tab) {
    case 'Details':
      return <DescriptionOutlinedIcon />;
    case 'Package inclusions':
      return <CardGiftcardOutlinedIcon />;
    case 'Inventory mapping':
      return <Inventory2OutlinedIcon />;
    case 'Package regions':
      return <PublicOutlinedIcon />;
    default:
      return undefined;
  }
}
